"use client";
import React, { useRef } from "react";
import { cva } from "class-variance-authority";
import { motion, useScroll, useTransform } from "motion/react";
import { TextBlockContent, type TextBlockContentProps } from "./Text";
import { Image } from "@/components/ui/Image";
import { Video } from "@/components/ui/Video";
import type { IconName, MediaData, ThemeVariant } from "@/types";
import { GradientContainer } from "@/components/ui/GradientContainer";

const mediaTextVariants = cva("relative overflow-hidden", {
  variants: {
    variant: {
      light: "",
      dark: "",
    },
  },
  defaultVariants: {
    variant: "light",
  },
});

export interface FullSizeTeaserTextProps extends Omit<
  TextBlockContentProps,
  "arrowDirection"
> {
  variant?: ThemeVariant;
  media?: MediaData;
  animate?: boolean;
  isDarkened?: boolean;
}

/**
 * MediaText component - combines media (image/video) with text content
 * Supports flexible positioning and sizing with grid layout, including optional device mockups
 */
export const FullSizeTeaser: React.FC<FullSizeTeaserTextProps> = ({
  media,
  topline,
  headline,
  text,
  headingTag = "h2",
  headingStyle = "h2",
  variant = "light",
  button,
  animate = false,
  isDarkened = true,
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: animate ? containerRef : undefined,
    offset: ["start end", "end end"],
  });

  // Animation transforms
  const width = useTransform(scrollYProgress, [0.4, 0.8], ["80%", "100%"]);
  const height = useTransform(scrollYProgress, [0.4, 0.8], ["80vh", "100vh"]);
  const borderRadius = useTransform(
    scrollYProgress,
    [0.6, 0.8],
    ["24px", "0px"]
  );
  const imageScale = useTransform(scrollYProgress, [0.8, 1], [1, 1.15]);

  // Override button color based on variant if not explicitly set
  const buttonVariant = button
    ? {
        label: button.label,
        link: button.link,
        icon: button.icon,
        color: "yellow" as const,
        variant: "secondary" as const,
      }
    : undefined;

  const renderMedia = () => {
    let content = null;

    if (!media) {
      content = (
        <GradientContainer className="absolute inset-0 w-full h-full"></GradientContainer>
      );
    } else if (media.type === "image" && media.image) {
      content = (
        <motion.div
          style={animate ? { scale: imageScale } : {}}
          className="absolute inset-0 w-full h-full"
        >
          <Image
            {...media.image}
            quality={80}
            fill
            objectFit="cover"
            sizes="100vw"
            className="w-full h-full"
            // priority // since most likely not on the fold we can load it lazily
          />
        </motion.div>
      );
    } else if (media.type === "video" && media.video) {
      content = (
        <motion.div
          style={animate ? { scale: imageScale } : {}}
          className="absolute inset-0 w-full h-full"
        >
          <Video video={media.video} className="w-full h-full object-cover" />
        </motion.div>
      );
    }

    if (!content) return null;

    return content;
  };

  const content = (
    <motion.div
      style={animate ? { width, height, borderRadius } : {}}
      className={mediaTextVariants({
        variant,
        className: `mx-auto flex flex-col justify-center ${
          !animate ? "w-full min-h-[70svh]" : ""
        } ${className}`,
      })}
    >
      {renderMedia()}
      {media && isDarkened && (
        <div
          className={
            "absolute inset-0 w-full h-full pointer-events-none bg-black/70"
          }
        />
      )}
      <div className={"container w-screen md:py-32 py-16"}>
        <div className={"relative md:col-span-9 col-span-10 flex items-center"}>
          <TextBlockContent
            topline={topline}
            headline={headline}
            text={text}
            headingTag={headingTag}
            headingStyle={headingStyle}
            variant={variant}
            button={buttonVariant}
          />
        </div>
      </div>
    </motion.div>
  );

  if (!animate) return content;

  return (
    <div ref={containerRef} className="relative h-[150vh] w-full">
      <div className="sticky top-0 h-screen flex items-center justify-center overflow-hidden">
        {content}
      </div>
    </div>
  );
};
